import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchTaskDecisionDetail } from '../store/statusSlice'
import type { TaskDecision } from '../store/statusSlice'
import './TaskRequestFeed.css'

function TaskDecisionItem({ decision }: { decision: TaskDecision }) {
  const [expanded, setExpanded] = useState(false)
  const dispatch = useAppDispatch()
  const detail = useAppSelector((state) => state.status.decisionDetails[decision.id])
  const pendingCount = decision.pending_count
  const completedCount = decision.completed_count
  const chosen = decision.chosen_task
  const chosenSkill = chosen?.skill?.split('/').pop()?.replace('.md', '') || ''

  useEffect(() => {
    if (expanded && !detail) {
      dispatch(fetchTaskDecisionDetail(decision.id))
    }
  }, [expanded, detail, dispatch, decision.id])

  return (
    <div className="task-request-feed__item" data-testid="task-decision-item">
      <button
        type="button"
        className="task-request-feed__item-header"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <span className="task-request-feed__time">
          {new Date(decision.created_at).toLocaleString()}
        </span>
        <span className="task-request-feed__app">{decision.app_id}</span>
        <span className="task-request-feed__counts">
          <span className="task-request-feed__count task-request-feed__count--pending">{pendingCount} pending</span>
          <span className="task-request-feed__count task-request-feed__count--completed">{completedCount} completed</span>
        </span>
        {chosen ? (
          <span className="task-request-feed__badge task-request-feed__badge--chosen" title={chosen.id}>
            {chosenSkill || 'ASSIGNED'}
          </span>
        ) : (
          <span className="task-request-feed__badge task-request-feed__badge--none">NONE READY</span>
        )}
        <span className="task-request-feed__toggle" aria-hidden="true">{expanded ? '\u2013' : '+'}</span>
      </button>
      {expanded && (
        <div className="task-request-feed__detail">
          {chosen && (
            <div className="task-request-feed__section">
              <div className="task-request-feed__section-title">Chosen Task</div>
              <pre className="task-request-feed__json">{JSON.stringify(chosen, null, 2)}</pre>
            </div>
          )}
          {!detail ? (
            <div className="task-request-feed__none">Loading details...</div>
          ) : (
            <>
              <div className="task-request-feed__section">
                <div className="task-request-feed__section-title">Pending Tasks ({pendingCount})</div>
                {detail.pending_tasks.length === 0 ? (
                  <div className="task-request-feed__none">None</div>
                ) : (
                  <pre className="task-request-feed__json">{JSON.stringify(detail.pending_tasks, null, 2)}</pre>
                )}
              </div>
              <div className="task-request-feed__section">
                <div className="task-request-feed__section-title">Completed Tasks ({completedCount})</div>
                {detail.completed_tasks.length === 0 ? (
                  <div className="task-request-feed__none">None</div>
                ) : (
                  <pre className="task-request-feed__json">{JSON.stringify(detail.completed_tasks, null, 2)}</pre>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function TaskRequestFeed() {
  const taskDecisions = useAppSelector((state) => state.status.taskDecisions)
  const [collapsed, setCollapsed] = useState(false)

  return (
    <section className="task-request-feed" data-testid="task-request-feed" aria-labelledby="task-request-feed-heading">
      <h2 className="sr-only" id="task-request-feed-heading">Recent Task Requests</h2>
      <button
        type="button"
        className="task-request-feed__title"
        data-testid="task-request-feed-toggle"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed(!collapsed)}
      >
        <span>Recent Task Requests</span>
        <span className="task-request-feed__toggle-icon" aria-hidden="true">{collapsed ? '+' : '\u2013'}</span>
      </button>
      {!collapsed && (
        taskDecisions.length === 0 ? (
          <p className="task-request-feed__empty" data-testid="task-request-feed-empty">
            No task requests yet
          </p>
        ) : (
          <div className="task-request-feed__list">
            {taskDecisions.map((decision) => (
              <TaskDecisionItem key={decision.id} decision={decision} />
            ))}
          </div>
        )
      )}
    </section>
  )
}

export default TaskRequestFeed
